import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * CollectionSettingsModal
 *
 * A centered modal opened from the Collection view that lets the owner:
 *   - Rename the collection (title field)
 *   - Edit the URL slug (claim, rename, or clear)
 *   - Make the collection public / private
 *   - Manage share permissions (list members, add by email, remove)
 *   - Copy a shareable link
 *   - Copy the public JSON API URL (published collections only)
 *
 * Permissions:
 *   - Renaming, slug editing, and member changes require admin / owner
 *     role (enforced server-side). For non-owners we still render the
 *     modal but the mutating fields are gated by the same checks the
 *     server applies, so the action button toasts the error instead of
 *     mutating silently.
 *   - The slug editor is hidden entirely for Primaries, implicit Groups,
 *     Stacks, and Primary-rooted Collections (the entity rejects the
 *     first three; the fourth has no URL surface until detach).
 *   - Members listing is visible to any reader.
 */
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { faXmark, faTrash, faUserPlus, faCircleNotch, faLink, faGlobe, faLock, faCode, } from "@fortawesome/pro-solid-svg-icons";
import { listCollectionMembers } from "@/api/query-api";
import { collectionEffectiveMembersQueryKey, collectionMembersQueryKey, } from "@/api/collection-members-query-key";
import { addMemberToCollection, addMemberToGroup, publishCollection, removeMemberFromCollection, removeMemberFromGroup, setCollectionSlug, unpublishCollection, updateCollection, } from "@/api/command-api";
import { collectionStore } from "@/data/collection-store";
import { API_BASE_URL } from "@/viewer-config";
import { absoluteUrl, useSlugContextForCollection, useUrlForCollection } from "@/lib/urls";
import { urlForCollection } from "@shared/types/urls";
import { isValidNameSlug, normalizeNameSlug } from "@shared/types/slug";
import "./collection-settings-modal.scss";
const MAX_TITLE_LENGTH = 200;
const ROLE_LABEL = {
    owner: "Owner",
    editor: "Editor",
    viewer: "Viewer",
};
export const CollectionSettingsModal = memo(({ collection, onClose, onCollectionUpdated, }) => {
    const queryClient = useQueryClient();
    const panelRef = useRef(null);
    const titleInputRef = useRef(null);
    const [title, setTitle] = useState(collection.title ?? "");
    const [slug, setSlug] = useState(collection.slug ?? "");
    const [addEmail, setAddEmail] = useState("");
    const [addRole, setAddRole] = useState("editor");
    // Resolve the parent from the **cross-layer** cache so we can see
    // Primaries (filtered out of `useList()`) and non-Primary Groups
    // alike. Without this, a Primary-rooted Collection would look like
    // it has no parent and the modal would fall into the "no destination"
    // catch-all.
    const parentCollection = collectionStore.useFindById(collection.parentCollectionId);
    useEffect(() => {
        setTitle(collection.title ?? "");
    }, [collection.id, collection.title]);
    useEffect(() => {
        setSlug(collection.slug ?? "");
    }, [collection.id, collection.slug]);
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose]);
    useEffect(() => {
        const input = titleInputRef.current;
        if (!input)
            return;
        // Skip auto-focus on phones — focusing a text input on iOS pops the
        // software keyboard the instant the modal opens, which (a) shoves the
        // sheet up under the address bar and (b) presumes the user came here
        // to rename. Desktop keeps the focus-+-select for fast keyboard rename.
        const isMobile = window.matchMedia("(max-width: 640px)").matches;
        if (isMobile)
            return;
        input.focus();
        input.select();
    }, []);
    const membersQuery = useQuery({
        queryKey: collectionMembersQueryKey(collection.id),
        queryFn: () => listCollectionMembers(collection.id),
        staleTime: 15_000,
    });
    const renameMutation = useMutation({
        mutationFn: (nextTitle) => updateCollection(collection.id, { title: nextTitle }),
        onSuccess: (updated) => {
            onCollectionUpdated?.(updated);
            void collectionStore.refreshCollections();
            showToast.success("Collection renamed");
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to rename collection");
        },
    });
    // Slug mutation. `undefined` clears, a string claims/renames. The
    // server validates the shape + uniqueness; we surface its error
    // message verbatim so reserved-word / collision hints stay specific.
    const slugMutation = useMutation({
        mutationFn: (input) => setCollectionSlug(collection.id, input.slug),
        onSuccess: (updated) => {
            onCollectionUpdated?.(updated);
            void collectionStore.refreshCollections();
            showToast.success(updated.slug !== undefined && updated.slug.length > 0
                ? "URL updated"
                : "URL cleared");
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to update URL");
        },
    });
    // Publish toggle. Primary-rooted Collections detach into the
    // standalone state first (parallel to the share-then-add flow),
    // since `publish` requires a standalone layer-1 row or a Group.
    // The detach step itself is idempotent — repeat clicks on an
    // already-detached collection are no-ops.
    const publishMutation = useMutation({
        mutationFn: async (input) => {
            if (input.publish === false) {
                return unpublishCollection(collection.id);
            }
            if (isPrimaryRooted) {
                await collectionStore.detachFromPrimary(collection.id);
            }
            return publishCollection(collection.id);
        },
        onSuccess: (updated, variables) => {
            onCollectionUpdated?.(updated);
            void collectionStore.refreshCollections();
            showToast.success(variables.publish
                ? "Collection is now public — anyone with the link can view"
                : "Collection is private again");
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to change collection visibility");
        },
    });
    // Members live in different places depending on the Collection's
    // current shape (under the standalone-Collections model, 2026-05-01):
    //
    //   1. Primary-rooted (private under user's Primary) → detach into
    //      the standalone state first, then add the member directly to
    //      the Collection with the caller's chosen role.
    //
    //   2. Standalone (no parent) → add directly to the Collection with
    //      the caller's chosen role.
    //
    //   3. Already in a shared Group → add directly to the parent Group
    //      via /v1/group/:id/add-member with the caller's chosen role.
    //
    // All three states use the same role schema (owner / editor /
    // viewer) since 2026-05-01-three-role; the role selector is shown
    // for every case.
    //
    // The modal's "members" listing shows effective members (including
    // inherited grants from the Group), so the UI doesn't need to know
    // which path was taken.
    //
    // `isStandalone` only cares about the Collection's own
    // `parentCollectionId` — the parent lookup might miss for stale
    // caches, but the Collection's parent field is authoritative.
    const isStandalone = collection.parentCollectionId === undefined;
    const isPrimaryRooted = parentCollection?.type === "primary";
    const isGroupRooted = parentCollection !== undefined && parentCollection.type !== "primary";
    const isPrimary = collection.type === "primary";
    // Publish gating. Mirrors the entity-layer guard in CollectionEntity
    // — only layer-0 Groups and standalone layer-1 Collections are
    // direct publish targets. Primary-rooted is offered as a
    // detach-then-publish flow (parallel to the share flow). Everything
    // else is read-only with an explanatory hint.
    const isImplicit = collection.isImplicit === true;
    const isGroup = collection.layerIndex === 0;
    const isStack = collection.layerIndex === 2;
    const isPublished = collection.publishedAt !== undefined;
    const canTogglePublish = isPublished
        ? // Always allow flipping off — even Group-rooted children that
            // somehow ended up with a row-level publishedAt (legacy data)
            // should be unsettable. The server still enforces canAdmin.
            true
        : isPrimary === false &&
            isImplicit === false &&
            isStack === false &&
            // Group-rooted: publication lives on the parent group; we
            // surface a hint and disable the toggle.
            isGroupRooted === false;
    const publishHintText = (() => {
        if (isPrimary)
            return "Primary collections can't be made public.";
        if (isImplicit)
            return "Auto-shared pair groups can't be made public.";
        if (isStack)
            return "Stacks inherit publication from their parent collection.";
        if (isGroupRooted)
            return "This collection inherits publication from its parent group.";
        if (isPublished)
            return "Anyone with the link can view this collection.";
        if (isPrimaryRooted) {
            return "Publishing detaches this collection from your Primary so it has a public URL.";
        }
        if (isGroup)
            return "Anyone with the link will be able to view this group.";
        return "Anyone with the link will be able to view this collection.";
    })();
    // Slug-editing gating. The entity layer rejects Primaries, implicit
    // Groups, and Stacks outright; Primary-rooted Collections are layer-1
    // (so `setSlug` would technically accept them) but the slug only
    // becomes part of any URL after detach, so we hide the editor until
    // the user shares/publishes. The slug also can't anchor a standalone
    // collection's public URL without a username — we surface a hint in
    // that case rather than blocking the edit, since users may want to
    // claim the slug first and the username right after.
    const isSlugEditable = isPrimary === false &&
        isImplicit === false &&
        isStack === false &&
        isPrimaryRooted === false;
    const slugCtx = useSlugContextForCollection(collection);
    const trimmedSlug = normalizeNameSlug(slug);
    const isClear = trimmedSlug.length === 0;
    const currentSlug = collection.slug ?? "";
    const slugChanged = trimmedSlug !== currentSlug;
    const slugShapeValid = isClear || isValidNameSlug(trimmedSlug);
    const slugPreviewUrl = (() => {
        if (isClear)
            return urlForCollection(collection, slugCtx);
        if (slugShapeValid === false)
            return undefined;
        // Preserve `parentCollectionId` so the preview picks the right
        // URL shape — a child collection with a not-yet-saved slug must
        // not preview as `/c/{slug}` (reserved for standalone rows).
        return urlForCollection({
            id: collection.id,
            slug: trimmedSlug,
            parentCollectionId: collection.parentCollectionId,
        }, slugCtx);
    })();
    const slugHintText = (() => {
        if (isClear) {
            return isGroup
                ? "Clear the slug to release the public URL — the group reverts to a member-only link."
                : "Clear the slug to revert to the member-only link.";
        }
        if (slugShapeValid === false) {
            return "Use 3–20 lowercase letters, digits, or hyphens. No leading, trailing, or repeated hyphens.";
        }
        if (isGroup) {
            return "Anyone with the link can find this group by name.";
        }
        return undefined;
    })();
    const canSaveSlug = isSlugEditable &&
        slugChanged &&
        slugShapeValid &&
        slugMutation.isPending === false;
    const addMemberMutation = useMutation({
        mutationFn: async (input) => {
            if (isPrimaryRooted) {
                // Detach-then-add: pull the Collection out of Primary into the
                // standalone state, then add the member directly to the
                // Collection with the caller's chosen role.
                await collectionStore.detachFromPrimary(collection.id);
                await addMemberToCollection(collection.id, input.email, input.role);
                return;
            }
            if (isStandalone) {
                await addMemberToCollection(collection.id, input.email, input.role);
                return;
            }
            if (isGroupRooted && parentCollection) {
                await addMemberToGroup(parentCollection.id, input.email, input.role);
                return;
            }
            throw new Error("Cannot determine where to add this member");
        },
        onSuccess: (_result, variables) => {
            setAddEmail("");
            void queryClient.invalidateQueries({ queryKey: collectionMembersQueryKey(collection.id) });
            void queryClient.invalidateQueries({
                queryKey: collectionEffectiveMembersQueryKey(collection.id),
            });
            void collectionStore.refreshCollections();
            showToast.success(`Invited ${variables.email} as ${variables.role}`);
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to add member");
        },
    });
    const removeMemberMutation = useMutation({
        mutationFn: async (accountId) => {
            // Removal target depends on the Collection's current shape:
            //   - Standalone → remove direct row on the Collection itself.
            //   - Group-rooted → remove on the parent Group.
            //   - Primary-rooted → no members to remove (Primary-cleanliness).
            if (isStandalone) {
                await removeMemberFromCollection(collection.id, accountId);
                return;
            }
            if (isGroupRooted && parentCollection) {
                await removeMemberFromGroup(parentCollection.id, accountId);
                return;
            }
            throw new Error("This collection has no removable members.");
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: collectionMembersQueryKey(collection.id) });
            void queryClient.invalidateQueries({
                queryKey: collectionEffectiveMembersQueryKey(collection.id),
            });
            void collectionStore.refreshCollections();
            showToast.success("Member removed");
        },
        onError: (err) => {
            showToast.error(err instanceof Error ? err.message : "Failed to remove member");
        },
    });
    const handleBackdropClick = useCallback((e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    }, [onClose]);
    const handleRenameSubmit = useCallback((e) => {
        e.preventDefault();
        const trimmed = title.trim();
        if (trimmed.length === 0 || trimmed.length > MAX_TITLE_LENGTH)
            return;
        if (trimmed === (collection.title ?? ""))
            return;
        renameMutation.mutate(trimmed);
    }, [title, collection.title, renameMutation]);
    const handleSlugSubmit = useCallback((e) => {
        e.preventDefault();
        if (canSaveSlug === false)
            return;
        slugMutation.mutate({ slug: isClear ? undefined : trimmedSlug });
    }, [canSaveSlug, isClear, trimmedSlug, slugMutation]);
    const handleAddMemberSubmit = useCallback((e) => {
        e.preventDefault();
        const email = addEmail.trim();
        if (email.length === 0)
            return;
        addMemberMutation.mutate({ email, role: addRole });
    }, [addEmail, addRole, addMemberMutation]);
    // Single source of truth for the share/copy URL — the same string
    // backs both the "Copy link" button and the rendered share-row.
    // `useUrlForCollection` picks the prettiest URL the live cache can
    // support: `/{owner-or-group-name}/{collection-slug}` when the
    // slug + parent context are both available, falling back to the
    // member-only `/g/{id}` otherwise. Both forms are valid share
    // targets — the member-only URL works for any invited member, the
    // slug URL additionally works for anonymous visitors of a
    // published collection.
    const shareLinkPath = useUrlForCollection(collection) ?? "";
    const shareLink = shareLinkPath.length > 0 ? absoluteUrl(shareLinkPath) : "";
    const handleCopyLink = useCallback(() => {
        if (shareLink.length === 0)
            return;
        navigator.clipboard
            .writeText(shareLink)
            .then(() => showToast.success("Link copied"))
            .catch(() => showToast.error("Failed to copy link"));
    }, [shareLink]);
    // Public JSON export endpoint (`GET /v1/public/collection/:id/blocks`,
    // no auth) — only meaningful while the collection is published, so the
    // row below renders behind `isPublished`. ID-based (not slug-based) so
    // the URL keeps working if the slug is renamed or cleared.
    const publicApiUrl = `${API_BASE_URL}/v1/public/collection/${collection.id}/blocks`;
    const handleCopyApiUrl = useCallback(() => {
        navigator.clipboard
            .writeText(publicApiUrl)
            .then(() => showToast.success("API URL copied"))
            .catch(() => showToast.error("Failed to copy API URL"));
    }, [publicApiUrl]);
    const canRename = title.trim().length > 0 &&
        title.trim().length <= MAX_TITLE_LENGTH &&
        title.trim() !== (collection.title ?? "") &&
        !renameMutation.isPending;
    return (_jsx("div", { className: "collection-settings-modal__backdrop", role: "dialog", "aria-modal": "true", "aria-labelledby": "collection-settings-modal-title", onMouseDown: handleBackdropClick, children: _jsxs("div", { className: "collection-settings-modal", ref: panelRef, children: [_jsxs("header", { className: "collection-settings-modal__header", children: [_jsx("h2", { id: "collection-settings-modal-title", className: "collection-settings-modal__heading", children: "Collection settings" }), _jsx("button", { type: "button", className: "collection-settings-modal__close", onClick: onClose, "aria-label": "Close", children: _jsx(FaIcon, { icon: faXmark }) })] }), _jsxs("section", { className: "collection-settings-modal__section", children: [_jsx("h3", { className: "collection-settings-modal__section-title", children: "Name" }), _jsxs("form", { className: "collection-settings-modal__rename-form", onSubmit: handleRenameSubmit, children: [_jsx("input", { ref: titleInputRef, type: "text", className: "collection-settings-modal__title-input", value: title, onChange: (e) => setTitle(e.target.value), maxLength: MAX_TITLE_LENGTH, placeholder: "Untitled", disabled: isPrimary || renameMutation.isPending, "aria-label": "Collection name" }), _jsx("button", { type: "submit", className: "collection-settings-modal__primary-btn", disabled: !canRename || isPrimary, children: renameMutation.isPending ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : ("Save") })] }), isPrimary ? (_jsx("p", { className: "collection-settings-modal__hint", children: "Primary collections can't be renamed." })) : null] }), isSlugEditable && (_jsxs("section", { className: "collection-settings-modal__section", children: [_jsx("h3", { className: "collection-settings-modal__section-title", children: "URL" }), _jsxs("form", { className: "collection-settings-modal__rename-form", onSubmit: handleSlugSubmit, children: [_jsx("input", { type: "text", className: "collection-settings-modal__title-input collection-settings-modal__slug-input", value: slug, 
                                    // Live-lowercase: the server normalizes silently, so
                                    // showing uppercase letters in the input that won't
                                    // match the eventual URL would be a UX trap.
                                    onChange: (e) => setSlug(e.target.value.toLowerCase()), placeholder: "my-collection", maxLength: 20, disabled: slugMutation.isPending, autoCapitalize: "none", autoCorrect: "off", spellCheck: false, "aria-label": "Collection URL slug" }), _jsx("button", { type: "submit", className: "collection-settings-modal__primary-btn", disabled: canSaveSlug === false, children: slugMutation.isPending ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : ("Save") })] }), _jsxs("div", { className: "collection-settings-modal__slug-preview", children: [_jsx("span", { className: "collection-settings-modal__slug-preview-label", children: "Link preview" }), _jsx("code", { className: "collection-settings-modal__slug-preview-url", children: slugPreviewUrl !== undefined
                                        ? absoluteUrl(slugPreviewUrl)
                                        : "—" })] }), slugHintText !== undefined && (_jsx("p", { className: "collection-settings-modal__hint", children: slugHintText }))] })), _jsxs("section", { className: "collection-settings-modal__section", children: [_jsx("h3", { className: "collection-settings-modal__section-title", children: "Public access" }), _jsxs("div", { className: "collection-settings-modal__publish-row", children: [_jsxs("div", { className: "collection-settings-modal__publish-status", children: [_jsx(FaIcon, { icon: isPublished ? faGlobe : faLock, className: isPublished
                                                ? "collection-settings-modal__publish-icon collection-settings-modal__publish-icon--on"
                                                : "collection-settings-modal__publish-icon" }), _jsx("span", { className: "collection-settings-modal__publish-label", children: isPublished ? "Public" : "Members only" })] }), _jsx("button", { type: "button", className: isPublished
                                        ? "collection-settings-modal__secondary-btn"
                                        : "collection-settings-modal__primary-btn", onClick: () => publishMutation.mutate({ publish: isPublished === false }), disabled: canTogglePublish === false || publishMutation.isPending, "aria-label": isPublished ? "Make private" : "Make public", children: publishMutation.isPending ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : isPublished ? ("Make private") : ("Make public") })] }), _jsx("p", { className: "collection-settings-modal__hint", children: publishHintText })] }), _jsxs("section", { className: "collection-settings-modal__section", children: [_jsx("h3", { className: "collection-settings-modal__section-title", children: "Share" }), _jsxs("form", { className: "collection-settings-modal__add-form", onSubmit: handleAddMemberSubmit, children: [_jsx("input", { type: "email", className: "collection-settings-modal__email-input", placeholder: "name@example.com", value: addEmail, onChange: (e) => setAddEmail(e.target.value), maxLength: 320, disabled: addMemberMutation.isPending || isPrimary, "aria-label": "Member email", required: true }), !isPrimary ? (_jsxs("select", { className: "collection-settings-modal__role-select", value: addRole, onChange: (e) => setAddRole(e.target.value), disabled: addMemberMutation.isPending, "aria-label": "Role", children: [_jsx("option", { value: "owner", children: "Owner" }), _jsx("option", { value: "editor", children: "Editor" }), _jsx("option", { value: "viewer", children: "Viewer" })] })) : null, _jsx("button", { type: "submit", className: "collection-settings-modal__primary-btn", disabled: addEmail.trim().length === 0 || addMemberMutation.isPending || isPrimary, "aria-label": "Invite", children: addMemberMutation.isPending ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : (_jsxs(_Fragment, { children: [_jsx(FaIcon, { icon: faUserPlus }), _jsx("span", { children: "Invite" })] })) })] }), _jsx("ul", { className: "collection-settings-modal__members", children: membersQuery.isLoading ? (_jsxs("li", { className: "collection-settings-modal__members-status", children: [_jsx(FaIcon, { icon: faCircleNotch, spin: true }), " ", _jsx("span", { children: "Loading members\u2026" })] })) : membersQuery.isError ? (_jsx("li", { className: "collection-settings-modal__members-status collection-settings-modal__members-status--error", children: "Failed to load members" })) : (membersQuery.data ?? []).length === 0 ? (_jsx("li", { className: "collection-settings-modal__members-status", children: "No members yet" })) : ((membersQuery.data ?? []).map((member) => {
                                const displayName = member.email ?? member.username ?? member.accountId;
                                const isOwner = member.role === "owner";
                                const isRemoving = removeMemberMutation.isPending &&
                                    removeMemberMutation.variables === member.accountId;
                                return (_jsxs("li", { className: "collection-settings-modal__member", children: [_jsxs("div", { className: "collection-settings-modal__member-main", children: [_jsx("span", { className: "collection-settings-modal__member-name", children: displayName }), _jsx("span", { className: `collection-settings-modal__member-role collection-settings-modal__member-role--${member.role}`, children: ROLE_LABEL[member.role] })] }), _jsx("button", { type: "button", className: "collection-settings-modal__member-remove", onClick: () => removeMemberMutation.mutate(member.accountId), disabled: isOwner || isRemoving || isPrimary, "aria-label": `Remove ${displayName}`, title: isOwner ? "Owners can't be removed" : "Remove", children: isRemoving ? (_jsx(FaIcon, { icon: faCircleNotch, spin: true })) : (_jsx(FaIcon, { icon: faTrash })) })] }, member.accountId));
                            })) }), _jsxs("div", { className: "collection-settings-modal__share-link", children: [_jsx("input", { type: "text", readOnly: true, value: shareLink, className: "collection-settings-modal__share-link-input", onClick: (e) => e.target.select(), "aria-label": "Shareable link" }), _jsxs("button", { type: "button", className: "collection-settings-modal__secondary-btn", onClick: handleCopyLink, children: [_jsx(FaIcon, { icon: faLink }), _jsx("span", { children: "Copy link" })] })] }), isPublished ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "collection-settings-modal__share-link", children: [_jsx("input", { type: "text", readOnly: true, value: publicApiUrl, className: "collection-settings-modal__share-link-input", onClick: (e) => e.target.select(), "aria-label": "Public API URL" }), _jsxs("button", { type: "button", className: "collection-settings-modal__secondary-btn", onClick: handleCopyApiUrl, children: [_jsx(FaIcon, { icon: faCode }), _jsx("span", { children: "Copy API URL" })] })] }), _jsx("p", { className: "collection-settings-modal__hint", children: "Returns this collection's blocks (including its stacks) as JSON. No access token required while the collection is public." })] })) : null] })] }) }));
});
